<template>
    <modal @close="closeWindow" dialog="modal-lg" :class="['reservation-modal dark-top refund-general-modal']">
        <div slot="title">
                <h2>{{ isPaymentScoped ? 'Payment Refund' : 'Global Refund' }}</h2>
                <h3 v-if="createdAt" class="mb-1 light">Created at {{ createdAt }}</h3>
        </div>

        <div slot="body">
            <div class="p-4">
                <div v-if="isTransactionRefund">
                    <h5>Payment</h5>
                    <div class="form-group">
                        <v-select
                            :options="transactionPaymentOptions"
                            label="label"
                            :clearable="false"
                            placeholder="Select payment"
                            v-model="selectedTransactionPaymentOption"
                            @input="onTransactionPaymentChange">
                        </v-select>
                    </div>
                </div>

                <!-- Area - Channel Selector -->
                <div :class="{ 'mt-3': isTransactionRefund }">
                    <h5>Channel</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.refundChannels.$error }">
                        <channel-selector v-model="refundObject.refundChannels" @input="channelChanged" :reservation="getSelectedReservation" />

                        <!-- Errors -->
                        <div v-if="$v.refundObject.refundChannels.$error" class="messages">
                            <span v-if="!$v.refundObject.refundChannels.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Is this a refund request -->
                <div class="mt-3">
                    <h5>When to refund</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.isRequest.$error, 'opacity-50': disableRefundRequestArea}">
                        <div class="whenToRefund radius-normal">
                            <div class="whenToRefund__option d-flex align-items-center p-2 pointer" @click="clickRefundRequestType(false)">
                                <div class="mx-2">
                                    <radio name="isRequest" :disabled="disableRefundRequestArea" :value="false" v-model="refundObject.isRequest" @input="$v.refundObject.isRequest.$touch"></radio>
                                </div>
                                <div class="darker">Refund customer now</div>
                            </div>

                            <div class="whenToRefund__option d-flex align-items-center p-2 pointer" @click="clickRefundRequestType(true)">
                                <div class="mx-2">
                                    <radio name="isRequest" :disabled="disableRefundRequestArea" :value="true" v-model="refundObject.isRequest" @input="$v.refundObject.isRequest.$touch"></radio>
                                </div>
                                <div class="darker">Request refund</div>
                            </div>
                        </div>

                        <div v-if="$v.refundObject.isRequest.$error" class="messages">
                            <span v-if="!$v.refundObject.isRequest.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Payment Method -->
                <div class="mt-3">
                    <h5>Payment Method</h5>
                    <div class="form-group" :class="{ error: $v.refundObject.paymentType.$error }">
                        <payment-method-picker :allowedMethods="allowdPaymentMethods" @change="paymentMethodPickerChange" v-model="refundObject.paymentType" />

                        <!-- Errors -->
                        <div v-if="$v.refundObject.paymentType.$error" class="messages">
                            <span v-if="!$v.refundObject.paymentType.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Refund Type-->
                <div class="mt-3">
                    <h5>Refund type</h5>
                    <div class="form-group" :class="{ error: $v.selectedRefundType.$error }">
                        <div class="d-flex flex-wrap">
                            <button
                                type="button"
                                v-for="refundType in getRefundTypes"
                                :key="refundType.id"
                                v-tooltip="refundTypeDisabledReason(refundType)"
                                class="refundTypeBtn btn bg-lightest darker bold mr-2 mb-2"
                                :class="{ 'selected': isRefundTypeSelected(refundType), 'disabled': disableRefundTypeArea || isRefundTypeDisabled(refundType) }"
                                @click="selectRefundType(refundType)"
                                :disabled="isRefundTypeDisabled(refundType)">
                                {{refundType.label}}
                            </button>
                        </div>

                        <!-- Errors -->
                        <div v-if="$v.selectedRefundType.$error" class="messages">
                            <span v-if="!$v.selectedRefundType.required">Select one.</span>
                        </div>
                    </div>
                </div>

                <!-- Sub option for refund type -->
                <div v-if="selectedRefundType != null" class="">
                    <!-- Area - Type -> Precentage or Value -->
                    <div v-if="selectedRefundType.id == 1" class="mt-3">
                        <h5>Percentage or Value</h5>
                        <div class="form-group" :class="{ error: $v.selectedRefundType.$error }">
                            <div class="percentValue col-sm-12 d-flex align-items-center">
                                <div class="percentValue__label bold mr-2">Value:</div>

                                <div class="d-flex flex-column">
                                    <div class="d-flex">
                                        <input
                                            type="number"
                                            class="percentValue__input glued-right"
                                            v-model.number="refundObject.amount"
                                            :max="isEuroMode ? refundMaximumValue : 100"
                                            :step="isEuroMode ? 0.01 : 1"
                                            :min="0"
                                            @input="onAmountInput"
                                            @keydown="preventDecimalInput"
                                            v-numFloat
                                        >
                                        <v-select class="percentValue__select glued-left" :options="getPercentageOrCurrency" v-model="selectedValueType" @input="valueTypeChanged($event)"></v-select>
                                    </div>
                                    <div v-if="isEuroMode && refundMaximumValue != null" class="fs-smaller darker mt-1">
                                        Max: {{ formatPrice(refundMaximumValue) }} €
                                    </div>
                                </div>
                            </div>

                            <!-- Errors -->
                            <div v-if="$v.selectedRefundType.$error" class="messages">
                                <span v-if="!$v.selectedRefundType.isValid">
                                    <span v-if="isEuroMode && refundMaximumValue != null">Value must be between 0 and {{ formatPrice(refundMaximumValue) }} €.</span>
                                    <span v-else>Invalid value.</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Area - Type -> Reduce Quantity (Pax and Extras)-->
                    <div v-if="selectedRefundType.id == 2" class="mt-3">
                        <h5>Reservation Items</h5>
                        <div class="form-group" :class="{ error: $v.refundItems.$error }">
                            <div class="col-sm-12">
                                <div class="w-100 text-center py-4" v-if="calculationResult == null">
                                    <loader />
                                </div>
                                <div v-else>
                                    <reduce-quantity v-model="refundObject.refundItems" v-if="calculationResult != null" :calculations="calculationResult" :compact="true" @change="requestCalculations()" />

                                    <div class="releaseQty d-flex align-items-center p-2 radius-normal bg-lighter pointer" :class="{ 'releaseQty--disabled': releaseQuantityBlocked }" @click="toggleReleaseQuantity">
                                        <div class="mx-2">
                                            <checkbox v-model="refundObject.isToReleaseQuantity" :disabled="releaseQuantityBlocked"></checkbox>
                                        </div>
                                        <div class="darker">
                                            <div class="releaseQty__title bold">Make refunded items available again</div>
                                            <div class="releaseQty__subtitle">This will allow to re-sell refunded items</div>
                                            <div v-if="releaseQuantityBlocked" class="releaseQty__warning">
                                                The reservation must keep at least one passenger.
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Errors -->
                                    <div v-if="$v.refundItems.$error" class="messages">
                                        <span v-if="!$v.refundItems.isValid">Select at least one item to refund.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Area - Reason -->
                <div class="mt-3">
                    <h5>Reason <span class="color-6">*</span></h5>
                    <div class="form-group" :class="{ error: $v.refundObject.refundReasonType.$error }">
                        <v-select :options="getRefundReasons" :disabled="disableReasonArea" label="text" placeholder="Select reason" v-model="selectedReason" @input="reasonChanged"></v-select>

                        <div v-if="refundObject.refundReasonType == 0" class="mt-3">
                            <label>Describe the refund reason</label>
                            <input type="text" class="w-100" v-model="refundObject.reason">
                        </div>

                        <!-- Errors -->
                        <div v-if="$v.refundObject.refundReasonType.$error" class="messages">
                            <span v-if="!$v.refundObject.refundReasonType.required">Reason is required.</span>
                        </div>
                    </div>
                </div>

                <!-- Area - Value to Refund -->
                <div class="mt-3">
                    <div class="bold dark text-right">
                        <loader v-if="isLoadingCalculations" />
                        <div v-else class="d-flex flex-column">
                            <div v-if="refundValueProcessed.now != null" class="d-flex flex-grow-1 justify-content-end">
                                <span>Refund now: <span class="color-1">{{ formatPrice(refundValueProcessed.now) }} €</span></span>
                            </div>
                            <div v-if="refundValueProcessed.later != null" class="d-flex flex-grow-1 justify-content-end">
                                <span>Refund later: <span class="color-1">{{ formatPrice(refundValueProcessed.later) }} €</span></span>
                            </div>
                            <div v-if="refundMaximumValue != null && refundValue > refundMaximumValue" class="messages d-flex flex-grow-1 justify-content-end mt-1">
                                <span>Exceeds maximum refundable amount of {{ formatPrice(refundMaximumValue) }} €.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div slot="footer" class="align-items-center">
            <div class="color-6 w-100 text-align-right">
                {{ emailWarningMessage }}
            </div>
            <button type="button" class="btn cancel" :disabled="isSubmitting" @click="clickCancel">Cancel</button>
            <button type="button" class="btn ok green" :class="{'disabled':isRefundValid == false || isSubmitting}" @click="clickRefund" :disabled="getIsLoadingCalculations || isRefundValid == false || isSubmitting">
                <loader v-if="isSubmitting" size="small" />
                <template v-else>Confirm</template>
            </button>
        </div>
    </modal>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { required, email, minLength, not, requiredUnless } from 'vuelidate/lib/validators'
    import paymentMethodPicker from '@/tools/modal/ReservationRefundTransaction/PaymentMethodSelector'
    import Modal from './Modal'
    import extendedVSelect from '@/extends/vue-select'
    import Loader from '@/tools/loaders/loader1'
    import _ from 'lodash'

    import channelSelector from '@/tools/modal/ReservationRefundTransaction/ChannelSelector'
    import reduceQuantity from '@/components/reservations/refund/ReduceQuantity'

    import Vue from 'vue'
    import { reservationsMixin } from '@/mixins/reservations'
    import { dialogMixin } from '@/mixins/dialog'
    import refundService from '@/api/reservation/refundService'
    import { PAYMENT_STATUS_PARTIALLY_PAID } from '@/utils/refundHelpers'
    import { RESERVATION_STATUS } from '@/enums/reservationStatus'

    export default {
        mixins: [ reservationsMixin, dialogMixin ],
        created() {
            this.debouncedValueChanged = _.debounce(this.valueChanged, 500);
            // Select percent
            this.selectedValueType = this.getPercentageOrCurrency[0];

            if (this.getRefundTransactionCode != null) {
                this.transactionContextCode = this.getRefundTransactionCode;
                this.transactionPaymentOptions = this.buildTransactionPaymentOptions();
                this.selectedTransactionPaymentOption = this.transactionPaymentOptions
                    .find(option => option.transactionCode != null) || null;
            }

            this.initRefundMaximumValue();

            // Search for existing not saved refund (id undefined or 0)
            let foundRefund = this.getSelectedReservation.refunds.find(refund => typeof refund.id == "undefined" || refund.id == 0);

            // Use a virgin refund or reuse an existing one (id = 0)
            if (!foundRefund || this.getSelectedReservation.refunds.length == 0) {
                this.$set(this, 'refundObject', JSON.parse(JSON.stringify(this.refundObjectClean)));
                this.refundObject.userAccount = this.account;
            } else {
                // Find the refund with id 0 (or without id)
                this.refundObject = foundRefund;

                // Select the correct refundType
                if (foundRefund.isToRefundTotal == true) {
                    this.selectedRefundType = this.getRefundTypes.find(refundType => refundType.id == 0);
                } else if (foundRefund.refundItems.length > 0) {
                    this.selectedRefundType = this.getRefundTypes.find(refundType => refundType.id == 2);
                } else {
                    this.selectedRefundType = this.getRefundTypes.find(refundType => refundType.id == 1);
                }

                // Select the correct parcial refund type
                this.selectedValueType = this.getPercentageOrCurrency.find(item => item.id == this.refundObject.amountType);
                this.refundObject.amountType;

                // Select correct reason
                this.selectedReason = this.getRefundReasons.find(reason => reason.id == this.refundObject.refundReasonType);

                // Request calculations
                this.requestCalculations();
            }
        },
        mounted() {
            this.selectedReason = this.getRefundReasons[0];
            this.refundObject.isToReleaseQuantity = !this.releaseQuantityBlocked;
        },
        beforeDestroy() {
            this.setRefundPaymentId(null);
            this.setRefundTransactionCode(null);
        },
        data() {
            return {
                selectedRefundType: null,
                selectedValueType: null,
                selectedPaymentMethod: null,
                selectedReason: null,
                refundObjectClean: {
                    refundChannels: [],
                    reason: null,
                    refundReasonType: null,
                    amount: 0,
                    amountType: 1, // Start with percentage (1) //amountTypeEnum.code
                    isToReleaseQuantity: false,
                    isToRefundAtSource: false,
                    isToRefundTotal: false,
                    isRequest: null, // Was null but now every refund is a request so is set true
                    paymentType: null, // PaymentTypeEnum.cash
                    userAccount: null,
                    refundItems: []
                },
                createdAt: null,
                refundObject: null,
                transactionContextCode: null,
                transactionPaymentOptions: [],
                selectedTransactionPaymentOption: null,
                isLoadingCalculations: false,
                isSubmitting: false,
                refundValue: 0,
                refundMaximumValue: null,
                calculationResult: null,
                debouncedValueChanged: null
            }
        },
        validations: {
            selectedRefundType: {
                required,
                isValid: (val, vm) => {
                    if (val == null) return false;

                    const exceedsMax = vm.refundMaximumValue != null && vm.refundValue > vm.refundMaximumValue;
                    if (exceedsMax) return false;

                    if (val.id !== 1) return true;

                    // Partial - Percentage
                    if (vm.refundObject.amountType === 1) {
                        return Number(vm.refundObject.amount) > 0 && Number(vm.refundObject.amount) <= 100;
                    }

                    // Partial - Euro: check directly against max for immediate feedback
                    if (vm.refundMaximumValue == null) {
                        return Number(vm.refundObject.amount) > 0;
                    }
                    return Number(vm.refundObject.amount) > 0 && Number(vm.refundObject.amount) <= vm.refundMaximumValue;
                }
            },
            refundObject: {
                refundChannels: {
                    required,
                },
                refundReasonType: {
                    required,
                },
                paymentType: {
                    required,
                },
                isRequest: {
                    required: (val) => {
                        return val != null;
                    }
                },
            },
            refundItems: {
                isValid: (val, vm) => {
                    if (vm.selectedRefundType == null) {
                        return true
                    }

                    if (vm.selectedRefundType.id != 2) {
                        return true;
                    } else {
                        if (vm.refundObject.refundItems && vm.refundObject.refundItems.length > 0) {
                            return vm.refundObject.refundItems.filter(refundItem => refundItem.quantity > 0).length > 0;
                        } else {
                            return false;
                        }
                    }
                }
            }
        },
        watch: {
            releaseQuantityBlocked(blocked) {
                if (blocked) {
                    this.refundObject.isToReleaseQuantity = false;
                }
            }
        },
        computed: {
            ...mapGetters([
                'getSelectedReservation',
                'getReservationCalculations',
                'getRefundTypes',
                'getPercentageOrCurrency',
                'account',
                'getRefundAvailablePaymentMethods',
                'getRefundReasons',
                'getPaymentTypeListAll',
                'getIsLoadingCalculations',
                'getRefundPaymentId',
                'getRefundTransactionCode',
            ]),
            isPaymentScoped() {
                return this.getRefundPaymentId != null || this.getRefundTransactionCode != null;
            },
            scopedPayment() {
                const payments = this.getSelectedReservation.payments || [];

                if (this.getRefundTransactionCode != null) {
                    const group = payments.filter(p => p.transactionCode === this.getRefundTransactionCode);
                    return group.length > 0 ? this.mergeTransactionPayments(group) : null;
                }

                if (this.getRefundPaymentId != null) {
                    return payments.find(p => p.id === this.getRefundPaymentId) || null;
                }

                return null;
            },
            isTransactionRefund() {
                return this.transactionContextCode != null;
            },
            // Refundable pay-in payments used to decide which refund types a GLOBAL
            // refund (no payment/transaction scope) is allowed to offer.
            globalRefundPayments() {
                const payments = this.getSelectedReservation.payments || [];
                return payments.filter(p =>
                    p.paymentDirection === 0 &&
                    p.id &&
                    p.refundMaximumValue !== 0
                );
            },
            // Number of transaction groups across the refundable payments. Payments that
            // share a transactionCode count as one (matching the transaction-merge logic);
            // payments without a transactionCode are each their own group.
            globalRefundGroupCount() {
                const keys = new Set(
                    this.globalRefundPayments.map(p => this.globalRefundGroupKey(p))
                );
                return keys.size;
            },
            // True when the same item (pax or extra) was paid across more than one
            // payment (and therefore possibly more than one transaction), which makes
            // an item-value refund impossible to attribute to a single payment/transaction.
            globalRefundHasDuplicatedItems() {
                const itemPayments = new Map(); // namespaced itemId -> Set of payment ids

                this.globalRefundPayments.forEach(p => {
                    const items = [
                        ...(p.tickets || []).map(t => ({ ...t, ns: 't' })),
                        ...(p.components || []).map(c => ({ ...c, ns: 'c' })),
                    ];

                    items.forEach(item => {
                        if (item.itemId == null) return;
                        const namespacedId = `${item.ns}-${item.itemId}`;
                        if (!itemPayments.has(namespacedId)) {
                            itemPayments.set(namespacedId, new Set());
                        }
                        itemPayments.get(namespacedId).add(p.id);
                    });
                });

                return Array.from(itemPayments.values()).some(payments => payments.size > 1);
            },
            // True when any refundable pay-in payment is only partially paid. Such a
            // payment doesn't cover its items' full value, so an item-value refund
            // can't map whole items back to what was actually paid.
            globalRefundHasPartiallyPaidPayment() {
                return this.globalRefundPayments.some(p => p.paymentStatus === PAYMENT_STATUS_PARTIALLY_PAID);
            },
            // True when a percentage/value refund (not total, no item breakdown) was
            // already made on this reservation. Such a refund reduces the refundable
            // amount by an arbitrary value, so the remainder no longer maps to whole items.
            hasPercentageOrValueRefund() {
                const refunds = [
                    ...(this.getSelectedReservation.generalRefunds || []),
                    ...(this.getSelectedReservation.refunds || []),
                ];

                return refunds.some(refund => {
                    // Skip the in-progress refund being edited (no real id yet).
                    if (!refund || !refund.id) {
                        return false;
                    }

                    const isTotal = refund.isToRefundTotal === true;
                    const hasItems =
                        (Array.isArray(refund.refundItems) && refund.refundItems.length > 0) ||
                        (Array.isArray(refund.refundDetailsItems) && refund.refundDetailsItems.length > 0);

                    return !isTotal && !hasItems;
                });
            },
            // Refund type ids available for a global refund:
            //  - single payment & transaction     -> Total, Percentage/Value, Item Value
            //  - multiple payments/transactions    -> Total, Item Value
            // Percentage/Value (1) requires a single payment in a single transaction —
            // with more than one payment or transaction there's no single amount to
            // apply it against.
            // Item Value (2) is additionally removed when:
            //  - the same item was paid across more than one payment/transaction
            //    (can't tell which payment/transaction to refund from), or
            //  - a percentage/value refund was already made, or
            //  - a payment is only partially paid (items aren't fully covered).
            globalAllowedRefundTypeIds() {
                let allowed;

                const isSinglePayment =
                    this.globalRefundPayments.length <= 1 && this.globalRefundGroupCount <= 1;

                if (isSinglePayment) {
                    allowed = [0, 1, 2];
                } else {
                    allowed = [0, 2];
                }

                if (this.globalRefundHasDuplicatedItems || this.hasPercentageOrValueRefund || this.globalRefundHasPartiallyPaidPayment) {
                    allowed = allowed.filter(id => id !== 2);
                }

                return allowed;
            },
            disableRefundRequestArea() {
                return !this.refundObject.refundChannels || this.refundObject.refundChannels.length < 1;
            },
            disableRefundTypeArea() {
                return this.refundObject.paymentType == null;
            },
            disableReasonArea() {
                return this.selectedRefundType == null;
            },
            refundValueProcessed() {
                if (this.refundObject.isRequest) {
                    return {
                        now:  0,
                        later: this.refundValue
                    }
                } else {
                    return {
                        now: this.refundValue,
                        later: null
                    }
                }
            },
            allowdPaymentMethods() {
                // ALLOW ALL FOR ALL!!!!!!
                if (this.refundObject.refundChannels.length > 0 && this.refundObject.isRequest != null) {
                    return this.getPaymentTypeListAll.map(item => item.id);
                } else {
                    return [];
                }

                // TABLE
                //                 AGENT 	         MY TEAM 	    WIDGET 	           GYG 	       LOCALS
                //             Refund  request Refund  Request Refund 	Request Refund 	Request Refund 	Request
                // Cash        x                   x 		    a               a               a
                // Debit Card  x                   x 		    a               a               a
                // Credit Card x                   x 		    a               a               a
                // Paypal                              x
                // Paypal                              x
                // BankTransfer                        x
                // AgentDeposit       x                                                 x               x
                // Stripe                                               x
                // Redunicre                           x
                //-----------------------------------------------------------------------------------------------
                // a = Only admin can do it
                // x = "Anyone" can
                //-----------------------------------------------------------------------------------------------
                // Cash = 1
                // Debit Card = 2
                // Credit Card = 3
                // Paypal = 5
                // Bank Transfer = 6
                // Agent Deposit = -100
                // Stripe = -90
                // Redunicre = 7

                let output = [];
                // If no channel is selcted
                if (this.refundObject.refundChannels.length == 0 || this.refundObject.isRequest == null) {
                    return [];
                }

                let isRefund = this.refundObject.isRequest == true? false : true;
                let foundRefundAvailablePaymentMethod = this.getRefundAvailablePaymentMethods.find(item => item.channelType == this.refundObject.refundChannels[0].channelType);
                
                // Now we search for specific permissions for the logged user (example: admin -> accessType = 1)
                let userAccessType = this.account.accessType;
                let foundUserPermissions = foundRefundAvailablePaymentMethod.permissions.find(permission => permission.accessType == userAccessType);
                let currentUserPermissionRefund = [];
                let currentUserPermissionRequest = [];

                if (foundUserPermissions) {
                    currentUserPermissionRefund = foundUserPermissions.refund;
                    currentUserPermissionRequest = foundUserPermissions.request;
                }

                // Get generic permissions and Merge the user specific permissions with the "generic permissions".
                // This will result on repeated number on the arry but for our that is not a problem
                let refund = foundRefundAvailablePaymentMethod.refund.concat(currentUserPermissionRefund);
                let request = foundRefundAvailablePaymentMethod.request.concat(currentUserPermissionRequest);

                if (foundRefundAvailablePaymentMethod) {
                    if (isRefund) {
                        output = refund;
                    } else {
                        output = request;
                    }
                } else {
                    output = [];
                }

                return output;
            },
            exceedsMaximumValue() {
                return this.refundMaximumValue != null && this.refundValue > this.refundMaximumValue;
            },
            isRefundValid() {
                if (this.exceedsMaximumValue || this.refundValue <= 0) return false;
                return this.$v.selectedRefundType.$invalid == false && this.$v.refundObject.$invalid == false && this.$v.refundItems.$invalid == false;
            },
            soldPax() {
                if (this.refundObject.refundChannels.length > 0) {
                    return this.getSelectedReservation.bookingGenderPrices.filter(soldItem => soldItem.channelType == this.refundObject.refundChannels[0].channelType);
                } else {
                    return this.getSelectedReservation.bookingGenderPrices;
                }
            },
            soldExtras() {
                if (this.refundObject.refundChannels.length > 0) {
                    return this.getSelectedReservation.bookingComponents.filter(soldItem => soldItem.channelType == this.refundObject.refundChannels[0].channelType);
                } else {
                    return this.getSelectedReservation.bookingComponents;
                }
            },
            isEuroMode() {
                return this.refundObject && this.refundObject.amountType === 0;
            },
            // Passengers currently booked on the reservation (all channels).
            bookedPaxQuantity() {
                return (this.getSelectedReservation.bookingGenderPrices || [])
                    .reduce((sum, pax) => sum + (pax.quantityToBook || 0), 0);
            },
            // Passengers selected for refund in the current item-value refund.
            refundedPaxQuantity() {
                return ((this.refundObject && this.refundObject.refundItems) || [])
                    .filter(item => item.bookingGenderPrice)
                    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
            },
            // Releasing the refunded items would leave the reservation active with
            // zero passengers, which is not allowed.
            releaseQuantityBlocked() {
                return this.bookedPaxQuantity > 0 && this.refundedPaxQuantity >= this.bookedPaxQuantity;
            },
            emailWarningMessage() {
                return `Customer ${this.refundObject && this.refundObject.refundChannels && this.refundObject.refundChannels[0] && this.refundObject.refundChannels[0].channelType? 'and Agent':''} will receive an email with refund information.`;
            }
        },
        methods: {
            ...mapActions([
                'closeModal',
                'setReservationCalculations',
                'setSelectedReservation',
                'setRefundPaymentId',
                'setRefundTransactionCode',
                'refreshCurrentSelectedReservation',
            ]),
            // For a global item-value refund, find the transaction the refunded items
            // belong to by matching item ids against each payment's tickets/components.
            // Returns the first transaction code found (items may span several).
            resolveItemTransactionCode() {
                const items = (this.refundObject.refundItems || []).filter(item => item.quantity > 0);

                if (items.length === 0) {
                    return null;
                }

                const payments = (this.getSelectedReservation.payments || [])
                    .filter(p => p.paymentDirection === 0 && p.id && p.transactionCode != null);

                for (const item of items) {
                    for (const payment of payments) {
                        if (item.bookingGenderPrice) {
                            const matches = (payment.tickets || [])
                                .some(t => t.itemId === item.bookingGenderPrice.id);
                            if (matches) {
                                return payment.transactionCode;
                            }
                        } else if (item.bookingComponent) {
                            const matches = (payment.components || [])
                                .some(c => c.itemId === item.bookingComponent.id);
                            if (matches) {
                                return payment.transactionCode;
                            }
                        }
                    }
                }

                return null;
            },
            // Group key for a payment: payments sharing a transactionCode are one group.
            globalRefundGroupKey(payment) {
                return payment.transactionCode != null
                    ? `tx-${payment.transactionCode}`
                    : `pay-${payment.id}`;
            },
            isRefundTypeDisabled(refundType) {
                // Total (0) is always available.
                if (refundType.id !== 1 && refundType.id !== 2) {
                    return false;
                }

                // Global refund (no payment and no transaction scope): availability
                // depends on the reservation's transaction/item structure.
                if (this.getRefundPaymentId == null && this.getRefundTransactionCode == null) {
                    return !this.globalAllowedRefundTypeIds.includes(refundType.id);
                }

                // Payment/transaction-scoped refund: only a single-payment scope allows
                // partial refunds. A transaction-wide scope allows total only.
                return this.getRefundPaymentId == null;
            },
            refundTypeDisabledReason(refundType) {
                if (!this.isRefundTypeDisabled(refundType)) {
                    return null;
                }
                
                if (this.getRefundPaymentId == null && this.getRefundTransactionCode == null) {
                    if (refundType.id === 1) {
                        return 'Refunds by percentage or value are only available when the reservation has a single payment in a single transaction.';
                    }

                    if (refundType.id === 2) {
                        if (this.hasPercentageOrValueRefund) {
                            return 'Item value refunds are no longer available because a percentage or value refund was already made.';
                        }

                        if (this.globalRefundHasPartiallyPaidPayment) {
                            return 'Item value refunds are not available because a payment is only partially paid.';
                        }

                        return 'Item value refunds are not available because the same item was paid across multiple payments or transactions.';
                    }

                    return null;
                }

                return 'Refunds by percentage, value or item are only available when refunding a single payment.';
            },
            channelChanged() {
                // Clear refund type | Clear payment method
                this.refundObject.paymentType = null;

                if (this.selectedRefundType != null) {
                    this.selectedRefundType = null;
                    this.updateAvailableRefundItems();
                }
            },
            initRefundMaximumValue() {
                if (this.isPaymentScoped) {
                    const payment = this.scopedPayment;
                    this.createdAt = payment ? payment.createdOn : null;
                    this.refundMaximumValue = (payment && payment.refundMaximumValue != null)
                        ? payment.refundMaximumValue
                        : null;
                } else {
                    this.refundMaximumValue = this.getSelectedReservation.globalRefundMaximumValue;
                }
            },
            buildTransactionPaymentOptions() {
                const payments = (this.getSelectedReservation.payments || [])
                    .filter(p => p.transactionCode === this.transactionContextCode)
                    .filter(p => p.refundMaximumValue !== 0);

                const options = payments.map(p => ({
                    id: p.id,
                    transactionCode: null,
                    label: this.transactionPaymentLabel(p),
                }));

                options.push({
                    id: null,
                    transactionCode: this.transactionContextCode,
                    label: `All payments (transaction) - ${payments.length} payments`,
                });

                return options;
            },
            transactionPaymentLabel(payment) {
                const date = payment.createdOn ? this.formatDate(payment.createdOn) : '-';
                const method = (payment.allocations && payment.allocations.length === 1)
                    ? payment.allocations[0].paymentTypeDescription
                    : null;
                const value = `${this.formatPrice(payment.paidValue)}${payment.currency || ''}`;
                return [date, method, value].filter(Boolean).join(' • ');
            },
            onTransactionPaymentChange(option) {
                if (option == null) {
                    return;
                }

                // Scope is mutually exclusive: a single payment id OR the transaction code.
                this.setRefundPaymentId(option.id);
                this.setRefundTransactionCode(option.transactionCode);

                // Allowed refund types depend on the scope, so reset the downstream selection.
                this.selectedRefundType = null;
                this.refundObject.isToRefundTotal = false;
                this.refundObject.amount = 0;
                this.$set(this.refundObject, 'refundItems', []);
                this.refundValue = 0;

                this.initRefundMaximumValue();
            },
            // Aggregates the payments of a transaction (sharing a transaction code) into a
            // single payment-like object, summing refundable values and concatenating the
            // tickets/components so a transaction refund behaves like a single-payment one.
            mergeTransactionPayments(group) {
                const primary = group[0];
                const sumOrNull = (key) => {
                    const values = group.map(p => p[key]).filter(v => v != null);
                    return values.length > 0 ? values.reduce((sum, v) => sum + v, 0) : null;
                };

                return {
                    ...primary,
                    refundMaximumValue: sumOrNull('refundMaximumValue'),
                    tickets: group.flatMap(p => p.tickets || []),
                    components: group.flatMap(p => p.components || []),
                };
            },
            // Sums the quantities actually paid per item across the refundable pay-in
            // payments. A global refund uses this so the item-value list only offers
            // items that were paid for, not the whole booking.
            paidQuantitiesByItem() {
                const map = new Map();

                this.globalRefundPayments.forEach(payment => {
                    (payment.tickets || []).forEach(ticket => {
                        if (ticket.itemId == null) return;
                        const key = `t-${ticket.itemId}`;
                        map.set(key, (map.get(key) || 0) + (Number(ticket.quantity) || 0));
                    });
                    (payment.components || []).forEach(component => {
                        if (component.itemId == null) return;
                        const key = `c-${component.itemId}`;
                        map.set(key, (map.get(key) || 0) + (Number(component.quantity) || 0));
                    });
                });

                return map;
            },
            // Sums the quantities already refunded per item across saved item-value refunds.
            refundedQuantitiesByItem() {
                const map = new Map();
                const seenRefundIds = new Set();

                const refundLists = [
                    ...(this.getSelectedReservation.generalRefunds || []),
                    ...(this.getSelectedReservation.refunds || []),
                ];

                refundLists.forEach(refund => {
                    // Only saved refunds; skip the in-progress one and avoid double counting
                    // a refund that appears in both lists.
                    if (!refund || !refund.id || seenRefundIds.has(refund.id)) {
                        return;
                    }
                    seenRefundIds.add(refund.id);

                    // Refunds that released their quantity ("make available again") already
                    // reduced the booked/paid quantity, so they're reflected in paidMap.
                    // Subtracting them again here would remove the item twice.
                    if (refund.isToReleaseQuantity === true) {
                        return;
                    }

                    const items = (refund.refundDetailsItems && refund.refundDetailsItems.length > 0)
                        ? refund.refundDetailsItems
                        : (refund.refundItems || []);

                    items.forEach(item => {
                        const qty = Number(item.quantity) || 0;
                        if (qty <= 0) {
                            return;
                        }

                        let key = null;
                        if (item.bookingGenderPrice && item.bookingGenderPrice.id != null) {
                            key = `t-${item.bookingGenderPrice.id}`;
                        } else if (item.bookingComponent && item.bookingComponent.id != null) {
                            key = `c-${item.bookingComponent.id}`;
                        }
                        if (key == null) {
                            return;
                        }

                        map.set(key, (map.get(key) || 0) + qty);
                    });
                });

                return map;
            },
            createRefundItemsArray() {
                let output = [];
                let cleanPax = [];
                let cleanExtras = [];

                const payment = this.scopedPayment;
                // For a global refund (no scoped payment) restrict to paid items and
                // discount quantities already refunded.
                const refundedMap = payment ? null : this.refundedQuantitiesByItem();
                const paidMap = payment ? null : this.paidQuantitiesByItem();

                // PAX
                if (this.soldPax && this.soldPax.length > 0) {
                    cleanPax = JSON.parse(JSON.stringify(this.soldPax));
                    cleanPax = cleanPax.filter(pax => {return pax.quantityToBook > 0});
                    
                    cleanPax = cleanPax.map(pax => {
                        // If payment exists, sum matching tickets in payment.tickets (a transaction
                        // aggregates several payments, so the same item can appear more than once).
                        let paymentTicketQuantity;

                        if (payment && payment.tickets && payment.tickets.length > 0) {
                            const matchingTickets = payment.tickets.filter(ticket => ticket.itemId === pax.id);
                            if (matchingTickets.length > 0) {
                                paymentTicketQuantity = matchingTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);
                            }
                        }
                        if(payment){
                            pax.quantityToBook = paymentTicketQuantity || 0;
                        } else {
                            // Global refund: offer only what was paid, minus what was already
                            // refunded WITHOUT releasing quantity. Released refunds are excluded
                            // by refundedQuantitiesByItem() because their effect is already baked
                            // into paidMap, so subtracting them here would remove the item twice.
                            const paidQty = paidMap.get(`t-${pax.id}`) || 0;
                            const refundedQty = refundedMap.get(`t-${pax.id}`) || 0;
                            pax.quantityToBook = Math.max(0, paidQty - refundedQty);
                        }
                        return {
                            refundType: null,
                            quantity: 0,
                            bookingGenderPrice: pax
                        }
                    }).filter(item => item.bookingGenderPrice.quantityToBook > 0);
                }

                // EXTRAS
                if (this.soldExtras && this.soldExtras.length > 0) {
                    cleanExtras = JSON.parse(JSON.stringify(this.soldExtras));
                    
                    cleanExtras = cleanExtras.map(extra => {
                        // If payment exists, sum matching components in payment.components (a transaction
                        // aggregates several payments, so the same item can appear more than once).
                        let paymentTicketQuantity;

                        if (payment && payment.components && payment.components.length > 0) {
                            const matchingComponents = payment.components.filter(component => component.itemId === extra.id);
                            if (matchingComponents.length > 0) {
                                paymentTicketQuantity = matchingComponents.reduce((sum, c) => sum + (c.quantity || 0), 0);
                            }
                        }
                        if(payment){
                            extra.quantity = paymentTicketQuantity || 0;
                        } else {
                            // Global refund: offer only what was paid, minus what was already
                            // refunded WITHOUT releasing quantity. Released refunds are excluded
                            // by refundedQuantitiesByItem() because their effect is already baked
                            // into paidMap, so subtracting them here would remove the item twice.
                            const paidQty = paidMap.get(`c-${extra.id}`) || 0;
                            const refundedQty = refundedMap.get(`c-${extra.id}`) || 0;
                            extra.quantity = Math.max(0, paidQty - refundedQty);
                        }
                        return {
                            refundType: null,
                            quantity: 0,
                            bookingComponent: extra
                        }
                    }).filter(item => item.bookingComponent.quantity > 0);
                }

                output = output.concat(cleanPax, cleanExtras);

                return output;
            },
            paymentMethodPickerChange(e) {
                if (e == null) {
                    this.refundObject.paymentType = null;
                }
                this.$v.refundObject.paymentType.$touch;
            },
            valueTypeChanged(e) {
                this.refundObject.amountType = e.id;
                this.updateAmountResumeValue();
                this.requestCalculations();
            },
            valueChanged() {
                this.updateAmountResumeValue();
                this.requestCalculations();
            },
            updateAmountResumeValue() {
                const newValue = this.refundObject.amount !== 0 ? 0 : this.total;
                this.$set(this.refundObject, 'amountResumeValue', newValue);
            },
            updateAvailableRefundItems() {
                this.refundObject.refundItems = this.createRefundItemsArray();
                this.requestCalculations();
            },
            selectRefundType(refundType) {
                if (this.disableRefundTypeArea || this.isRefundTypeDisabled(refundType)) {
                    return false;
                }

                this.selectedRefundType = refundType;

                // Set total payment or parcial payment
                const isTotalOrPartial = refundType.id === 0 || refundType.id === 1;

                if (isTotalOrPartial) {
                    this.$set(this.refundObject, 'refundItems', []);
                    this.refundObject.isToRefundTotal = refundType.id === 0;
                    this.refundObject.amount = refundType.id === 0 ? null : 0;
                    this.updateAmountResumeValue();
                    this.requestCalculations();
                } else if (refundType.id === 2) {
                    // Reduce quantity
                    this.updateAvailableRefundItems();
                    this.refundObject.amount = null;
                    this.refundObject.isToRefundTotal = false;
                }
            },
            isRefundTypeSelected(refundType) {
                return this.selectedRefundType != null && this.selectedRefundType.id == refundType.id;
            },
            clickRefundRequestType(e) {
                if (this.disableRefundRequestArea) {
                    return false;
                }

                // Reset downstream settings
                this.selectedRefundType = null;
                this.refundObject.paymentType = null;

                this.refundObject.isRequest = e;
                this.$set(this.refundObject, 'paymentType', null);
                this.$v.refundObject.isRequest.$touch;
            },
            toggleReleaseQuantity() {
                if (this.releaseQuantityBlocked) {
                    return;
                }

                this.refundObject.isToReleaseQuantity = !this.refundObject.isToReleaseQuantity;
            },
            clickCancel() {
                this.closeModal();
            },
            updateReservation() {
                if (this.refundObject.isToReleaseQuantity == true) {
                    // Update Reservation PAX and Extra count
                    if (this.refundObject.refundItems && this.refundObject.refundItems.length > 0) {
                        // Iterate all refund items
                        for (let refundItem of this.refundObject.refundItems) {
                            // Is Pax
                            if (refundItem.bookingGenderPrice) {
                                // Find the same Pax on reservation and update its quantityToBook
                                let foundReservationGenderPrice = this.getSelectedReservation.bookingGenderPrices.find(bookingGenderPrice => {
                                    return bookingGenderPrice.id == refundItem.bookingGenderPrice.id;
                                });

                                if (foundReservationGenderPrice) {
                                    foundReservationGenderPrice.quantityToBook -= refundItem.quantity;

                                    // Reduce the value of paid and ticket price (totalProductGrossprice, totalGrossprice) based on reduced quantity
                                    this.getReservationCalculations.totalProductGrossprice -= (foundReservationGenderPrice.grossprice * refundItem.quantity);
                                    this.getReservationCalculations.totalGrossprice -= (foundReservationGenderPrice.grossprice * refundItem.quantity);
                                }
                            } else if (refundItem.bookingComponent) {
                                // Is Extra | Find the same Extra on reservation and update its quantity
                                let foundReservationExtra = this.getSelectedReservation.bookingComponents.find(bookingComponent => {
                                    return bookingComponent.id == refundItem.bookingComponent.id
                                });

                                if (foundReservationExtra) {
                                    foundReservationExtra.quantity -= refundItem.quantity;
                                }
                            }
                        }
                    }
                }

                // Update stored reservation
                this.setSelectedReservation({payload: this.getSelectedReservation, isToResetFlags: false});
            },
            reasonChanged(e) {
                this.refundObject.refundReasonType = e.id;
            },
            addRefundToReservation(refund, targetReservation) {
                // Search if we have a current editing refund
                let foundEditingRefund = targetReservation.refunds.find(reservationRefund => {
                    return typeof reservationRefund.id == 'undefined' || reservationRefund.id == 0
                });

                if (foundEditingRefund) {
                    foundEditingRefund = refund;
                } else {
                    targetReservation.refunds.push(refund);
                }
            },
            calculateRefundValueLocally() {
                let value = 0;

                if (this.selectedRefundType && this.selectedRefundType.id === 0) {
                    value = this.refundMaximumValue || 0;
                } else if (this.selectedRefundType && this.selectedRefundType.id === 1) {
                    if (this.refundObject.amountType === 1) {
                        // Percentage
                        value = this.refundMaximumValue != null
                            ? Math.floor(this.refundMaximumValue * Number(this.refundObject.amount)) / 100
                            : 0;
                    } else {
                        // Euro value
                        value = Number(this.refundObject.amount) || 0;
                    }
                }

                this.refundValue = value;
                this.refundObject.amountResumeValue = value;
            },
            requestCalculations() {
                if (this.isPaymentScoped && (!this.selectedRefundType || this.selectedRefundType.id !== 2)) {
                    this.calculateRefundValueLocally();
                    return;
                }

                this.isLoadingCalculations = true;
                this.refundObject.refundReasonType = this.selectedReason.id;

                let tempReservation = JSON.parse(JSON.stringify(this.getSelectedReservation));

                // Add refund to reservation
                this.addRefundToReservation(this.refundObject, tempReservation);
                this.setReservationCalculations({
                    reservation:tempReservation,
                    onlyCalculate: true
                }).then(response => {
                    if (response && response.data) {
                        this.calculationResult = response.data;
                        if (response.data.refund && response.data.refund.refundMaximumValue != null) {
                            this.refundMaximumValue = response.data.refund.refundMaximumValue;
                        }
                        let cappedRefundValue = response.data.refund.amountResumeValue;
                        if (this.refundMaximumValue != null && cappedRefundValue > this.refundMaximumValue) {
                            cappedRefundValue = this.refundMaximumValue;
                        }
                        this.refundValue = cappedRefundValue;
                        this.refundObject.amountResumeValue = cappedRefundValue;
                    }
                    this.isLoadingCalculations = false;
                }).catch(err => {
                    this.isLoadingCalculations = false;
                })
            },
            addPayout(refund) {
                let payoutObject = {
                    tempRefundPayout: true, // We use this to know if this payout comes from the current refund, if we edit the refund we update this payout
                    currency: "€", // Calculations always returns the € value so we can hardcode this
                    description: "Refund",
                    paymentDirection: 1, // 1 = out | 1 = in
                    treasurerUserAccount: this.account,
                    type: this.refundObject.paymentType, // Payment method
                    value: this.refundValue,
                    refundId: refund.id,
                    refunds: [refund]
                }

                // Search for a tempRefundPayout payout
                let tempPayout = this.getSelectedReservation.payments.find(payment => payment.tempRefundPayout == true);
                if (tempPayout) {
                    let paymentIndex = this.getSelectedReservation.payments.indexOf(tempPayout);
                    this.getSelectedReservation.payments[paymentIndex] = payoutObject;
                } else {
                    this.getSelectedReservation.payments.push(payoutObject);
                }
            },
            onAmountInput(e) {
                let value = e.target.value;

                if (this.isEuroMode) {
                    value = parseFloat(value);

                    if (!isNaN(value)) {
                        value = Math.floor(value * 100) / 100;
                    }
                } else {
                    value = parseInt(value);

                    if (!isNaN(value)) {
                        value = Math.max(0, Math.min(100, value));
                    }
                }

                this.refundObject.amount = isNaN(value) ? null : value;

                this.debouncedValueChanged();
            },
            preventDecimalInput(e) {
                if (!this.isEuroMode) {
                    if (e.key === '.' || e.key === ',' ) {
                        e.preventDefault();
                    }
                }
            },
            async clickRefund() {
                this.$v.$touch();

                if (this.isRefundValid == false) {
                    return false;
                }

                if (this.exceedsMaximumValue) {
                    Vue.toasted.error(`Refund value exceeds the maximum allowed amount of ${this.formatPrice(this.refundMaximumValue)} €.`);
                    return false;
                }

                // Refund requests stay pending and can still be canceled; only direct refunds are final
                if (!this.refundObject.isRequest) {
                    const confirmed = await this.$confirm('<h1>Confirm refund</h1><p class="mt-3">Are you sure you want to confirm this refund?<br><b>This action is irreversible!</b></p>');
                    if (!confirmed) return false;
                }

                this.isSubmitting = true;
                this.$set(this.refundObject, 'refundDetailsItems', this.refundObject.refundItems);
                
                try {
                    const payload = {
                        bookingNumber: this.getSelectedReservation.id,
                        refund: this.refundObject,
                        cancelBooking: this.getSelectedReservation.changeStatusTo == RESERVATION_STATUS.CANCELED
                    }

                    const result = await refundService.createRefund(payload)

                    if (!result.ok) {
                        const beMessage = result.error && result.error.response && result.error.response.data && result.error.response.data.error;
                        Vue.toasted.error(beMessage || 'Something went wrong. Please try again.');
                        return
                    }

                    Vue.toasted.success('Refund created successfully.')
                    this.addPayout(result.value);
                    await this.refreshCurrentSelectedReservation();
                    this.eventHub.$emit('updateThisReservation', this.getSelectedReservation);
                    this.closeModal();
                } finally {
                    this.isSubmitting = false;
                }
            },
            closeWindow() {
                this.setRefundPaymentId(null);
                this.setRefundTransactionCode(null);
                this.setReservationCalculations(this.getSelectedReservation);
                this.closeModal();
            },
        },
        components: {
            Modal,
            Loader,
            paymentMethodPicker,
            vSelect: extendedVSelect,
            reduceQuantity,
            channelSelector
        }
    }
</script>

<style lang="scss">
    @import '../../assets/scss/_variables.scss';

    .refund-general-modal {
        align-items: flex-start;
        padding-top: 3rem;
    }

    .refund-general-modal .modal-dialog {
        width: 100%;
        max-width: 760px;
    }

    .refund-general-modal .modal-content {
        display: flex;
        flex-direction: column;
        max-height: calc(100vh - 7rem);
    }

    .refund-general-modal .modal-header,
    .refund-general-modal .modal-footer {
        flex-shrink: 0;
    }

    .refund-general-modal .modal-body {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;

        scrollbar-gutter: stable;

        /* Visible scrollbar so users can tell there is more content */
        scrollbar-width: thin;                 /* Firefox */
        scrollbar-color: $medium transparent;  /* Firefox */

        &::-webkit-scrollbar {                 /* Chrome / Safari */
            width: 0.5rem;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: $medium;
            border-radius: 0.25rem;
        }
    }
</style>

<style lang="scss" scoped>
    @import "~@/assets/scss/_utility.scss";
    @import "~@/assets/scss/_variables.scss";

    .refundTypeBtn {
        @extend .border-medium;
        height: 40px;
        border-radius: 0.4rem !important;

        &:hover {
            @extend .bg-lighter;
        }

        &.selected {
            border-color: $color-1 !important;
            background-color: rgba($color-1, 0.12) !important;
        }

        &.disabled,
        &:disabled {
            opacity: 0.5;
            background-color: $light;
            cursor: not-allowed !important;
        }
    }

    .whenToRefund {
        border: 1px solid $light;
        border-radius: 0.75rem;
        background: $lightest;
        overflow: hidden;

        &__option {
            & + & {
                border-top: 1px solid $light;
            }

            &:hover {
                background: $lighter;
            }
        }
    }

    .percentValue {
        &__label {
            font-size: 0.85rem;
        }

        &__input {
            width: 95px !important;
            font-size: 0.85rem;
        }

        &__select {
            width: 64px !important;

            ::v-deep .selected-tag {
                padding-right: 1.5rem;
            }
        }
    }

    .releaseQty {
        padding: 0.4rem 0.5rem !important;

        .mx-2 {
            transform: scale(0.8);
            transform-origin: left center;
        }

        &__title {
            font-size: 0.75rem;
        }

        &__subtitle {
            font-size: 0.65rem;
        }

        &__warning {
            font-size: 0.65rem;
            color: $orange;
            margin-top: 2px;
        }

        &--disabled {
            cursor: default !important;

            .releaseQty__title,
            .releaseQty__subtitle {
                opacity: 0.5;
            }
        }
    }
</style>


// WEBPACK FOOTER //
// ReservationRefundGeneral.vue?35f5805c